import type { SOPGenerationActivity } from '@/types';

/**
 * Reading a run's activity stream as thoughts rather than lines.
 *
 * Kept apart from the component that renders it so both panels — and anything that only
 * needs the shape of the stream, like the header's "what is it doing right now" — can use
 * it without importing a component.
 */

/** Consecutive activity lines sharing a step name, read as one thought. */
interface ThoughtGroup {
	step: string;
	lines: SOPGenerationActivity[];
	at: string;
}

/**
 * Fold the stream into groups.
 *
 * A run reports many small lines — what it read, what the model gave back, how each check
 * came out — and a flat list of them reads as noise. Grouped by the phase that produced
 * them, the same stream reads as the generator thinking out loud, which is what makes a
 * two-minute run watchable instead of a spinner.
 */
export function groupActivity(activity: SOPGenerationActivity[]): ThoughtGroup[] {
	const groups: ThoughtGroup[] = [];
	for (const entry of activity) {
		const last = groups[groups.length - 1];
		if (last && last.step === entry.step) last.lines.push(entry);
		else groups.push({ step: entry.step, lines: [entry], at: entry.at });
	}
	return groups;
}

/** Format an ISO timestamp as a short local time, the only part worth showing. */
export function shortTime(iso: string): string {
	const parsed = new Date(iso);
	return Number.isNaN(parsed.getTime())
		? ''
		: parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
